import React, { useEffect, useRef } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import CloseIcon from '@mui/icons-material/Close';
import { useCloseable } from '@nhl/react-hooks/lib/useCloseable';

import { statsConfigSelectors } from '../../../redux/ducks/config';
import { playersAndTeamsActionCreators } from '../../../redux/ducks/playersAndTeams';
import { puckAndPlayerTrackingSelectors } from '../../../redux/ducks/puckAndPlayerTracking';
import { commonMessages } from '../../../utility/commonMessages';
import pptLegend from '../../pages/puckAndPlayerTracking/legend';
import { pptMessages } from '../../pages/puckAndPlayerTracking/pptMessages';

import {
    LegendContainer,
    Legend,
    LegendItem,
    LegendLink,
    LegendLabel,
    LegendClose,
    DialogContainer,
    LegendList,
} from './styles';

// This is a functional component since we're controlling whether it appears or not in DataAndFilters
const LegendComponent = () => {
    const dialogRef = useRef();
    const dispatch = useDispatch();
    const intl = useIntl();

    const statsConfig = useSelector((state) => state.config);
    const appliedReport = useSelector((state) => state.playersAndTeams.report.applied);
    const isLegendActive = useSelector((state) => state.playersAndTeams.isLegendActive);
    const pptPage = puckAndPlayerTrackingSelectors.isPuckAndPlayerTrackingPage();

    const hideLegend = () => {
        dialogRef.current?.close();
        dispatch(playersAndTeamsActionCreators.showLegend(false));
    };

    useCloseable({ closeableRef: dialogRef, closeFunction: hideLegend, shouldStopListening: !isLegendActive });

    useEffect(() => {
        if (isLegendActive) {
            dialogRef.current?.showModal();
        }
    }, [isLegendActive]);

    // Compute the legend if a report has been applied or this is a PPT page
    if (!isLegendActive || (Object.keys(appliedReport).length === 0 && !pptPage)) {
        return null;
    }

    const reservedKeys = [
        'franchiseId',
        'franchiseName',
        'gameId',
        'homeRoad',
        'opponentTeamAbbrev',
        'playerId',
        'isInHallOfFame',
        'active',
        'teamId',
    ];

    // Get the correct column titles for either the game or season portion of the report
    const { displayItems } = pptPage
        ? pptLegend
        : statsConfigSelectors.getTimeSpecificDataOnReport(statsConfig, appliedReport);
    const legendItemListItems = displayItems
        .filter((key) => !reservedKeys.includes(key))
        .map((key) => {
            const glossaryKey = intl.formatMessage(pptPage ? pptMessages.header[key] : commonMessages.columns[key]);
            const glossaryVal = intl.formatMessage(pptPage ? pptMessages.tooltips[key] : commonMessages.tooltips[key]);
            const glossaryUrl = `glossary?abbreviation=${encodeURIComponent(glossaryKey)}&name=${encodeURIComponent(glossaryVal)}`;

            return (
                <LegendItem key={key}>
                    <LegendLink href={glossaryUrl}>
                        <>&bull; </>
                        <div>
                            {glossaryKey}: {glossaryVal}
                        </div>
                    </LegendLink>
                </LegendItem>
            );
        });

    return (
        <>
            <DialogContainer />
            <LegendContainer ref={dialogRef}>
                <Legend ref={dialogRef}>
                    <LegendLabel>
                        <FormattedMessage {...commonMessages.labels.legend} />
                        <LegendClose onClick={hideLegend}>
                            <CloseIcon />
                        </LegendClose>
                    </LegendLabel>
                    <LegendList>
                        <ul>{legendItemListItems}</ul>
                    </LegendList>
                </Legend>
            </LegendContainer>
        </>
    );
};

export default LegendComponent;
